import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { BracketType, BracketTypeEnum } from "../Common/types";
import { Competitor } from "../Common/Competitors/types";
import { KataCalculationMethod } from "./disciplinePackages";

type BaseCategoryPodium = {
    parameter_id: number;
    category: string;
    categoryFinishTime: string;
    bracketAvailable: boolean;
    decorated: boolean;
    competitors: Record<string, Competitor>;
};

export type KataPerformance = {
    performanceNumber: number;
    judgeScores: number[];
    totalScore: number;
    discardMinMax: boolean;
    calculationMethod: KataCalculationMethod;
};

export type KataCompetitorDetails = {
    totalScore: number;
    performances: KataPerformance[];
};

export type KataCategoryPodium = BaseCategoryPodium & {
    bracketType: typeof BracketTypeEnum.Kata;
    details: Record<number, KataCompetitorDetails>;
};

export type StandardCategoryPodium = BaseCategoryPodium & {
    bracketType: Exclude<BracketType, typeof BracketTypeEnum.Kata>;
};

export type CategoryPodium = KataCategoryPodium | StandardCategoryPodium;

export const ResultsSortingEnum = {
    EndTime: "endTime",
    CategoriesOrder: "categoriesOrder",
} as const;
export type ResultsSorting = (typeof ResultsSortingEnum)[keyof typeof ResultsSortingEnum];

export const FifthPlaceCalculationEnum = {
    Two5thPlaces: "two5thPlaces",
    One5thOne6thPlace: "one5thOne6thPlace",
};

export type FifthPlaceCalculation = (typeof FifthPlaceCalculationEnum)[keyof typeof FifthPlaceCalculationEnum];

export type ResultsReportOptions = {
    fullList: boolean;
    resultsSorting: ResultsSorting;
    roundRobinWithTwo3rdPlaces: boolean;
    doubleEliminationWrestlingRules: boolean;
    doubleElimination5thPlaceCalculation?: FifthPlaceCalculation;
    twoPoolsWrestlingRules: boolean;
    twoPools5thPlaceCalculation?: FifthPlaceCalculation;
    repechageByFinalistsWrestlingRules: boolean;
    repechageByFinalists5thPlaceCalculation?: FifthPlaceCalculation;
};

export type SharedAcademyResultsResponse = Array<SharedAcademyResultWithCriteria>;

export const AcademyResultsRule = {
    ScoreForAwardedMedals: "scoreForAwardedMedals",
    ScoreForWonMatches: "scoreForWonMatches",
} as const;

export type AcademyResultsRuleType = (typeof AcademyResultsRule)[keyof typeof AcademyResultsRule];

export type ScoreForAwardedMedalsRule = {
    rule: typeof AcademyResultsRule.ScoreForAwardedMedals;
    input: {
        selectedCategories: number[];
        pointsForMedals: Record<number, number>;
        excludeOneAcademyCategories: boolean;
        excludeOneManCategories: boolean;
        useExtendedClassification: boolean;
    };
};

export type ScoreForWonMatchesRule = {
    rule: typeof AcademyResultsRule.ScoreForWonMatches;
    input: {
        selectedCategories: number[];
        pointsForWonMatch: number;
        multiplierForSubmissionWin: number | null;
    };
};

export type SelectedRule = ScoreForAwardedMedalsRule | ScoreForWonMatchesRule;

export type CalculateAcademyResultsRequest = {
    selectedRules: SelectedRule[];
    showExAequoPlaces: boolean;
    mergeAffiliations: boolean;
};

export type PublishAcademyResults = {
    name: string;
    criteria: CalculateAcademyResultsRequest;
};

export type ParentResultsMap = Record<number, SingleAcademyCalculation[]>;

export type AwardedMedalsBreakdown = {
    value: { medals: Record<number, number>; useExtendedClassification: boolean };
    type: "awardedMedalsBreakdown";
};
export type WonMatchesBreakdown = { value: { wonMatches: number }; type: "wonMatchesBreakdown" };

export type SingleAcademyCalculation = {
    academyId: number;
    academyName: string;
    ruleBreakdowns: Array<AwardedMedalsBreakdown | WonMatchesBreakdown>;
    points: number;
    place: number | null;
};

export type CalculatedAcademyResultsResponse = {
    mainResults: SingleAcademyCalculation[];
    parentResultsMap: ParentResultsMap;
};

export type PublicAcademyResult = SingleAcademyCalculation & {
    academyLogoImage: string | null;
    country: string | null;
};

export type PublicAcademyResults = {
    mainResults: PublicAcademyResult[];
    medalsSchema: number[];
    parentResultsMap: ParentResultsMap;
};

export type SharedAcademyResultWithCriteria = {
    id: number;
    created: string;
    name: string;
    criteria: CalculateAcademyResultsRequest;
};

export function convertResultsReportOptionsToURLSearchParams(options: ResultsReportOptions): URLSearchParams {
    const queryParams = new URLSearchParams();
    queryParams.append("fullList", options.fullList ? "1" : "0");
    queryParams.append("resultsSorting", options.resultsSorting);
    queryParams.append("roundRobinWithTwo3rdPlaces", options.roundRobinWithTwo3rdPlaces ? "1" : "0");
    queryParams.append("doubleEliminationWrestlingRules", options.doubleEliminationWrestlingRules ? "1" : "0");
    if (options.doubleEliminationWrestlingRules && options.doubleElimination5thPlaceCalculation) {
        queryParams.append("doubleElimination5thPlaceCalculation", options.doubleElimination5thPlaceCalculation);
    }
    queryParams.append("twoPoolsWrestlingRules", options.twoPoolsWrestlingRules ? "1" : "0");
    if (options.twoPoolsWrestlingRules && options.twoPools5thPlaceCalculation) {
        queryParams.append("twoPools5thPlaceCalculation", options.twoPools5thPlaceCalculation);
    }
    queryParams.append("repechageByFinalistsWrestlingRules", options.repechageByFinalistsWrestlingRules ? "1" : "0");
    if (options.repechageByFinalistsWrestlingRules && options.repechageByFinalists5thPlaceCalculation) {
        queryParams.append("repechageByFinalists5thPlaceCalculation", options.repechageByFinalists5thPlaceCalculation);
    }
    return queryParams;
}

const competitorResultsApi = {
    fetchCategoryPodiums(eventId: number, options: ResultsReportOptions): Promise<CategoryPodium[]> {
        const queryParams = convertResultsReportOptionsToURLSearchParams(options);

        return promiseRequest<CategoryPodium[]>({
            url: `/api/events/${eventId}/results/internal?${queryParams.toString()}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    fetchPublicCompetitorResults(eventId: number): Promise<CategoryPodium[]> {
        return promiseRequest<CategoryPodium[]>({
            url: `/api/events/${eventId}/results/public`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },

    updateDecoration(eventId: number, parameterId, decorated) {
        return promiseRequest({
            url: `/api/events/${eventId}/results/internal/${parameterId}/decorations`,
            method: "put",
            json: {
                decorated: !!decorated,
            },
        }).then(res => {
            if (res.statusCode !== 204) {
                throw Error("Could not update decoration");
            }
        });
    },

    calculateAcademyResults(eventId: number, request: CalculateAcademyResultsRequest): Promise<CalculatedAcademyResultsResponse> {
        return promiseRequest<CalculatedAcademyResultsResponse>({
            url: `/api/events/${eventId}/calculate-academy-results`,
            method: "post",
            json: request,
        }).then(resolveWithBody());
    },

    publishAcademyResults(eventId: number, request: PublishAcademyResults) {
        return promiseRequest({
            url: `/api/events/${eventId}/shared-academy-results`,
            method: "post",
            json: request,
        }).then(resolveNoContent());
    },

    getPublishedAcademyResults(eventId: number): Promise<SharedAcademyResultsResponse> {
        return promiseRequest<SharedAcademyResultsResponse>({
            url: `/api/events/${eventId}/shared-academy-results`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    deletePublishedAcademyResult(eventId: number, id: number) {
        return promiseRequest({
            url: `/api/events/${eventId}/shared-academy-results/${id}`,
            method: "delete",
            json: true,
        }).then(resolveNoContent());
    },

    getPublicAcademyResultsListing(eventId: number): Promise<SharedAcademyResultWithCriteria[]> {
        return promiseRequest<SharedAcademyResultWithCriteria[]>({
            url: `/api/events/${eventId}/results/public/academy-results`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },

    getSingleFullPublishedAcademyResults(eventId: number, academyResultId: number): Promise<PublicAcademyResults> {
        return promiseRequest<PublicAcademyResults>({
            url: `/api/events/${eventId}/results/public/academy-results/${academyResultId}`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    savePublicResultsReportOptions(eventId: number, resultsOptions: ResultsReportOptions) {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/results/options`,
            method: "put",
            json: resultsOptions,
        }).then(resolveNoContent());
    },
    getResultsReportOptions(eventId: number): Promise<ResultsReportOptions> {
        return promiseRequest<ResultsReportOptions>({
            url: `/api/admin/events/${eventId}/results/options`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
};

export default competitorResultsApi;
